import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AlertDialog, ButtonGroup, DialogTrigger } from '@adobe/react-spectrum';
import { ActionButton, Button } from '@react-spectrum/button';
import { Divider } from '@react-spectrum/divider';
import { Flex } from '@react-spectrum/layout';
import { View } from '@react-spectrum/view';
import Close from '@spectrum-icons/workflow/Close';
import Redo from '@spectrum-icons/workflow/Redo';
import Undo from '@spectrum-icons/workflow/Undo';
import { LogoInHTML } from 'app/assets/brands/Logo';
import { EditorActions } from 'app/stores/editor/actions';
import { HistoryActions } from 'app/stores/history/actions';
import { ProjectActions } from 'app/stores/project/actions';
import { getPagesCount } from 'app/stores/selectors';
import { useAppDispatch } from 'app/stores/store';
import { Download } from 'app/views/components/header/download';
import { Feedback } from 'app/views/components/header/feedback';
import { ManagePagesButtons } from 'app/views/components/header/platform/ManagePages';
import { useConnect } from 'app/views/hooks/useConnect';
import { useShortcuts } from 'app/views/hooks/useShortcuts';
import { analytics } from 'app/utils/analytics';
import { hasClipboard } from 'app/utils/caniuse';
import { prepareOnWriteToClipboard } from 'app/utils/clipboard';
import { DisabledActionButton } from '../../core/DisabledTooltip';
import { PreviewRender } from './DebugPreview';
import { PublishButton } from './Publish';
const Shortcuts = [
    {
        keys: ['esc', 'escape'],
        handler: 'cancel',
        isEnabled: () => true,
    },
    {
        keys: ['enter', 'return'],
        handler: 'apply',
        isEnabled: () => true,
    },
];
const Clipboard = () => {
    const dispatch = useAppDispatch();
    const onClipboard = React.useCallback(() => {
        analytics({
            action: 'ExportAction',
            label: 'copyToClipboard',
        });
        dispatch(ProjectActions.rasterizeProject({
            format: 'image/png',
            strategy: 'current_page',
        }))
            .unwrap()
            .then(output => prepareOnWriteToClipboard(output, dispatch));
    }, [dispatch]);
    const enabled = hasClipboard();
    if (!enabled) {
        return (React.createElement(DisabledActionButton, { label: "Clipboard", tooltip: "Clipboard is not available in your browser." }));
    }
    return (React.createElement(ActionButton, { "data-test-id": "header-clipboard", onPress: onClipboard, isQuiet: true }, "Clipboard"));
};
function CropControls() {
    const dispatch = useAppDispatch();
    const onCancel = React.useCallback(() => dispatch(EditorActions.resetAppController()), [dispatch]);
    const onApply = React.useCallback(() => dispatch(EditorActions.applyImageCrop()), [dispatch]);
    const shortcutsHandler = useCallback((_evt, _combo, shortcut) => {
        const key = String(shortcut.handler);
        switch (key) {
            case 'apply':
                onApply();
                break;
            case 'cancel':
                onCancel();
                break;
            default:
                break;
        }
    }, [onCancel, onApply]);
    useShortcuts(Shortcuts, shortcutsHandler);
    return (React.createElement(Flex, { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, justifyContent: "center", alignItems: "center" },
        React.createElement(ButtonGroup, null,
            React.createElement(Button, { variant: "secondary", onPress: onCancel }, "Cancel"),
            React.createElement(Button, { variant: "cta", onPress: onApply }, "Apply"))));
}
function ImageControls() {
    const dispatch = useAppDispatch();
    const { hasAnyPage, hasUndo, hasRedo } = useConnect(state => ({
        hasAnyPage: getPagesCount(state) > 0,
        hasUndo: state.project.inPast > 0,
        hasRedo: state.project.inFuture > 0,
    }));
    const onClose = React.useCallback(() => {
        analytics({
            action: 'ProjectAction',
            label: 'closeProject',
        });
        dispatch(EditorActions.closeProject());
    }, [dispatch]);
    const onUndo = React.useCallback(() => {
        analytics({
            action: 'ProjectAction',
            label: 'undo',
        });
        dispatch(HistoryActions.undo());
    }, [dispatch]);
    const onRedo = React.useCallback(() => {
        analytics({
            action: 'ProjectAction',
            label: 'redo',
        });
        dispatch(HistoryActions.redo());
    }, [dispatch]);
    if (!hasAnyPage) {
        return null;
    }
    const showPagePreview = configGet('showPagePreview', false);
    const multiSelectEnabled = configGet('multipleFilesSelectEnabled', false);
    return (React.createElement(Flex, { direction: "row", width: "100%", marginEnd: "size-175", gap: "size-125" },
        multiSelectEnabled && React.createElement(ManagePagesButtons, null),
        showPagePreview && React.createElement(PreviewRender, null),
        React.createElement(View, { flex: "2" }),
        React.createElement(ActionButton, { "data-test-id": "header-undo-btn", onPress: onUndo, isDisabled: !hasUndo, isQuiet: true },
            React.createElement(Undo, null)),
        React.createElement(ActionButton, { "data-test-id": "header-redo-btn", onPress: onRedo, isDisabled: !hasRedo, isQuiet: true },
            React.createElement(Redo, null)),
        React.createElement(Divider, { orientation: "vertical", size: "S" }),
        React.createElement(Feedback, { type: "popover", content: "Feedback" }),
        React.createElement(Divider, { orientation: "vertical", size: "S" }),
        React.createElement(Clipboard, null),
        React.createElement(Download, null),
        React.createElement(Divider, { orientation: "vertical", size: "S" }),
        React.createElement(PublishButton, null),
        React.createElement(Divider, { orientation: "vertical", size: "S" }),
        React.createElement(DialogTrigger, null,
            React.createElement(ActionButton, { "data-test-id": "header-close", isQuiet: true },
                React.createElement(Close, { size: "XS" })),
            React.createElement(AlertDialog, { variant: "destructive", title: "Close project", primaryActionLabel: "Close", cancelLabel: "Cancel", onPrimaryAction: onClose }, "This will permanently delete project and close image file. Continue?"))));
}
export function HeaderView() {
    const activeController = useSelector((state) => state.editor.activeController);
    let Controls = ImageControls;
    switch (activeController) {
        case 'image-crop':
            Controls = CropControls;
            break;
        case 'image-background':
            Controls = null;
            break;
        default:
            break;
    }
    return (React.createElement(View, { backgroundColor: "gray-50", borderBottomWidth: "thin", borderBottomColor: "gray-200" },
        React.createElement(Flex, { position: "relative", direction: "row", alignItems: "center", height: "100%" },
            React.createElement("a", { href: "/" },
                React.createElement(Flex, { direction: "row", marginX: "size-200", UNSAFE_className: "slogo", alignItems: "center" },
                    React.createElement(LogoInHTML, null),
                    React.createElement("span", null, "Annotely"))),
            Controls && React.createElement(Controls, null))));
}
